<template lang="pug">
    transition(name="fade")
        .item-form-parameter(v-if="!hidden")
            component(
                :is="getComponentName()"
                :parameter="parameter"
                v-model="item"
                :error="error"
                @input="processError"
            )
            p(
                class='mt-2 p-1 rounded text-sm'
                +' text-gray-500 bg-green-100'
                v-if="hasDescr"
                v-html="parameter['descr']"
            )
</template>

<script>

import store from 'js@/store'
import {mapMutations, mapGetters, mapState, mapActions} from "vuex";

import ItemFormGroupParameterText from 'cmp@/ItemFormGroupParameterText'
import ItemFormGroupParameterTextarea from 'cmp@/ItemFormGroupParameterTextarea'
import ItemFormGroupParameterCheckbox from 'cmp@/ItemFormGroupParameterCheckbox'
import ItemFormGroupParameterSlider from 'cmp@/ItemFormGroupParameterSlider'
import ItemFormGroupParameterSelectbox from 'cmp@/ItemFormGroupParameterSelectbox'
import ItemFormGroupParameterSelect from 'cmp@/ItemFormGroupParameterSelect'
import ItemFormGroupParameterUnits from 'cmp@/ItemFormGroupParameterUnits'
import ItemFormGroupParameterRadio from 'cmp@/ItemFormGroupParameterRadio'

export default {
    name: 'ItemFormGroupParameter',

    store,

    components: {
        ItemFormGroupParameterText,
        ItemFormGroupParameterTextarea,
        ItemFormGroupParameterCheckbox,
        ItemFormGroupParameterSlider,
        ItemFormGroupParameterSelectbox,
        ItemFormGroupParameterSelect,
        ItemFormGroupParameterUnits,
        ItemFormGroupParameterRadio,
    },

    props: {
        parameter: {
            type: Object,
        },
    },

    data() {
        return {
            firstCycle: true,
            hidden: true,
        }
    },

    async mounted() {
        this.setSortedParameter(this.parameter.id)
        if (!this.item) {
            this.setEmptyFormData(this.parameter.id)
        }

        if (this.isDepend()) {
            await this.checkDependentCondition()
            store.subscribe(mutation => {
                if (mutation.type === 'item/updateDependants') {
                    // console.info('vuex subscribe...')
                    this.checkDependentCondition(mutation.payload)
                }
            })
        } else {
            this.hidden = false
            this.processRequired()
        }
    },

    computed: {
        ...mapState({
            formStatus: state => state.item.formStatus,
            origins: state => state.item.origins,
        }),

        ...mapGetters({
            isFieldRequired: 'item/isFieldRequired',
            isExist: 'item/isExist',
            isHidden: 'item/isHidden',
            getValueOf: 'item/getValueOf',
            getErrorById: 'item/getErrorById',
        }),

        item: {
            get() {
                return this.getValueOf(this.parameter.id)
            },
            set(value) {
                this.updateFormData({key: this.parameter.id, value})
                if (this.origins.includes(this.parameter.id)) {
                    this.updateDependants(this.parameter.id)
                }
            },
        },

        error() {
            return this.getErrorById(this.parameter.id)
        },

        hasDescr() {
            return this.parameter['descr']
                && this.parameter['descr'].length > 0
        },
    },

    watch: {
        hidden: {
            handler (status) {
                if (this.firstCycle === true) {
                    if (status) {
                        this.setEmptyFormData(this.parameter.id)
                    }
                } else {
                    this.firstCycle = false
                }
            },
        },

        formStatus: {
            handler (status) {
                if (status.description === 'checkForm') {
                    this.processError()
                }
            },
        },
    },

    methods: {
        processError () {
            if (this.parameter['required']
                && this.isFieldRequired(this.parameter['id'])) {
                if (!this.isExist(this.parameter.id)) {
                    if (!this.error) {
                        this.addError({id: this.parameter.id})
                    }
                } else {
                    if (this.error) {
                        this.removeError(this.parameter.id)
                    }
                }
            }
        },

        ...mapMutations({
            updateRequiredFields: 'item/updateRequiredFields',
            setEmptyFormData: 'item/setEmptyFormData',
            updateDependants: 'item/updateDependants',
            setHidden: 'item/setHidden',
            addError: 'item/addError',
            removeError: 'item/removeError',
            setSortedParameter: 'item/setSortedParameter',
            updateFormData: 'item/updateFormData',
        }),

        ...mapActions({
            getDependentOptions: 'item/getDependentOptions',
        }),

        checkHidden (valueParent) {
            // const data = JSON.parse(this.parameter['dependent_conditions'])
            const data = this.parameter['dependent_conditions']
            let visible = false
            switch (data.sign) {
                case '=':
                    visible = (String(data.value) === String(valueParent))
                    break
                case '<':
                    visible = (Number(data.value) > Number(valueParent))
                    break
                case '>':
                    visible = (Number(data.value) < Number(valueParent))
                    break
                case 'in':
                    visible = data.value.includes(valueParent)
                    break
            }
            return !visible
        },

        async checkDependentCondition (id = false) {
            const df = this.parameter['dependent_from_parameter_id']
            if (!id || df === id) {
                const valueParent = this.getValueOf(df)
                this.hidden = (this.isHidden(df))
                    ? true
                    : this.checkHidden(valueParent)
                this.processRequired()
                if (!this.hidden) {
                    if (this.parameter.hasOwnProperty('dependent_url') && this.parameter.dependent_url.length > 0) {

                        // console.info(this.parameter)

                        await this.getDependentOptions({
                            url: this.parameter['dependent_url'],
                            id: this.parameter['id'],
                            valueParent})
                    }
                }
            }
        },

        processRequired () {
            if (this.parameter['required']) {

                if (!this.isFieldRequired(this.parameter['id']) && !this.hidden) {
                    this.updateRequiredFields({
                        parameterId: this.parameter['id'],
                        add: true,
                    })
                }
                else if (this.isFieldRequired(this.parameter.id)
                    && this.hidden) {
                    this.updateRequiredFields({
                        parameterId: this.parameter.id,
                        add: false,
                    })
                }

            }
        },

        isDepend () {
            return !!(this.parameter.hasOwnProperty('dependent_from_parameter_id')
                && this.parameter.dependent_from_parameter_id
                && this.parameter.hasOwnProperty('dependent_conditions'));
        },

        getComponentName () {
            return `item-form-group-parameter-${this.parameter.type}`
        },
    },

}

</script>

<style lang="sass" scoped>

@import "../../sass/variables"

.fade-enter-active, .fade-leave-active
    transition: all .3s
.fade-enter, .fade-leave-to
    opacity: .3
    height: 0

</style>
